import { toast } from "react-toastify";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import I18n from "common/i18n";
import { btnDelay } from "common/utils";
import { DeleteMessageBox } from "common/elements";
import { PieceStatus } from "features/builder/types";
import { MOCK_MY_PIECES } from "features/builder/mockData";
import {
  MapCard,
  AddMapButton,
  CreateMapModal,
  MapDetailPage,
  MyMapsHeader,
} from "features/builder/elements";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  MyMapsFilter,
  PieceSortOrder,
} from "features/builder/pieceList";

export const MyMapsTab = () => {
  const navigate = useNavigate();
  const [pieces, setPieces] = useState<MockPiece[]>(MOCK_MY_PIECES);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<PieceSortOrder>(PieceSortOrder.DESC);
  const [filter, setFilter] = useState<MyMapsFilter>(MyMapsFilter.ALL);
  const [trashTarget, setTrashTarget] = useState<MockPiece | null>(null);

  const visibleMaps = useMemo(() => {
    let list = [...pieces];
    if (filter === MyMapsFilter.FAVORITE) {
      list = list.filter((m) => m.liked > 0);
    } else if (filter === MyMapsFilter.DRAFT) {
      list = list.filter((m) => m.status === PieceStatus.DRAFT_NEW);
    } else if (filter === MyMapsFilter.EDITING) {
      list = list.filter((m) => m.status === PieceStatus.EDITING);
    }
    list.sort((a, b) =>
      sortOrder === PieceSortOrder.DESC
        ? b.updated_at.localeCompare(a.updated_at)
        : a.updated_at.localeCompare(b.updated_at),
    );
    return list;
  }, [pieces, sortOrder, filter]);

  const selected = pieces.find((m) => m.piece_id === selectedId) ?? null;

  const handleCreate = () => {
    btnDelay(() => {
      setCreateOpen(false);
      navigate("/courses/builder");
    });
  };

  const handleAction = (piece: MockPiece) => (action: PieceCardAction) => {
    switch (action) {
      case PieceCardAction.DETAIL:
        setSelectedId(piece.piece_id);
        break;
      case PieceCardAction.EDIT:
        navigate("/courses/builder");
        break;
      case PieceCardAction.PLAY:
        toast(I18n.t("MSG_BUILDER_BTN_PLAY"));
        break;
      case PieceCardAction.COPY:
        toast(I18n.t("MSG_BUILDER_COPY_TOAST"));
        break;
      case PieceCardAction.HINT:
        toast(I18n.t("MSG_BUILDER_MENU_HINT"));
        break;
      case PieceCardAction.TO_TRASH:
        setTrashTarget(piece);
        break;
      default:
        break;
    }
  };

  const handleConfirmTrash = () => {
    if (!trashTarget) return;
    setPieces((prev) => prev.filter((m) => m.piece_id !== trashTarget.piece_id));
    if (selectedId === trashTarget.piece_id) setSelectedId(null);
    setTrashTarget(null);
    toast(I18n.t("MSG_BUILDER_TO_TRASH_TOAST"));
  };

  if (selected) {
    return (
      <MapDetailPage
        piece={selected}
        context={PieceListContext.MINE}
        onBack={() => setSelectedId(null)}
        onAction={handleAction(selected)}
      />
    );
  }

  return (
    <div className="flex-col-el relative w-full h-full p-4 gap-3">
      <MyMapsHeader
        count={visibleMaps.length}
        sortOrder={sortOrder}
        filter={filter}
        onToggleSort={() =>
          setSortOrder((o) =>
            o === PieceSortOrder.DESC ? PieceSortOrder.ASC : PieceSortOrder.DESC,
          )
        }
        onSelectFilter={setFilter}
      />

      <div className="flex-col-el flex-1 w-full overflow-y-auto invisible-scrollbar">
        <div className="flex-row-el flex-wrap items-start justify-start gap-4 pb-4">
          {visibleMaps.map((m) => (
            <MapCard
              key={m.piece_id}
              piece={m}
              context={PieceListContext.MINE}
              onAction={handleAction(m)}
            />
          ))}
        </div>
      </div>

      <AddMapButton onClick={() => setCreateOpen(true)} />

      <CreateMapModal
        visible={createOpen}
        defaultName={I18n.t("MSG_BUILDER_DEMO_BUTTON")}
        close={() => btnDelay(() => setCreateOpen(false))}
        onCreate={handleCreate}
      />

      <DeleteMessageBox
        visible={trashTarget != null}
        title={I18n.t("MSG_BUILDER_TO_TRASH_CONFIRM_TITLE")}
        message={
          trashTarget ? I18n.t("MSG_BUILDER_TO_TRASH_CONFIRM_MSG", { name: trashTarget.name }) : ""
        }
        close={() => setTrashTarget(null)}
        handleDelete={handleConfirmTrash}
      />
    </div>
  );
};
